import { useEffect, useRef, useState } from "react";
import { NavLink, Outlet, Link, useNavigate, useLocation, Navigate } from "react-router-dom";
import { LayoutDashboard, CalendarDays, Users, UserCog, Scissors, Receipt, Package, BarChart3, LogOut, Globe, Bell, Settings as SettingsIcon, RefreshCcw, Gem, Wallet, Eye, EyeOff, Lock } from "lucide-react";
import { useAuth } from "../../context/AuthContext";
import { useMode } from "../../context/ModeContext";
import { api } from "../../lib/api";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../../components/ui/dialog";
import PinGate from "./PinGate";

const navItems = [
  { to: "/admin", label: "Overview", icon: LayoutDashboard, end: true, modes: ["ceo", "manager"] },
  { to: "/admin/appointments", label: "Appointments", icon: CalendarDays, modes: ["ceo", "manager"] },
  { to: "/admin/billing", label: "Billing", icon: Receipt, modes: ["ceo", "manager"] },
  { to: "/admin/loyalty", label: "Loyalty", icon: Gem, modes: ["ceo", "manager"] },
  { to: "/admin/inventory", label: "Inventory", icon: Package, modes: ["ceo", "manager"] },
  { to: "/admin/finance", label: "Finance", icon: Wallet, modes: ["ceo"] },
  { to: "/admin/finance", label: "Expenses", icon: Wallet, modes: ["manager"] },
  { to: "/admin/reports", label: "Reports", icon: BarChart3, modes: ["ceo"] },
  { to: "/admin/customers", label: "Customers", icon: Users, modes: ["ceo", "manager"] },
  { to: "/admin/staff", label: "Staff", icon: UserCog, modes: ["ceo", "manager"] },
  { to: "/admin/services", label: "Services", icon: Scissors, modes: ["ceo", "manager"] },
  { to: "/admin/staff-day", label: "My Day", icon: CalendarDays, modes: ["staff"] },
  { to: "/admin/settings", label: "Settings", icon: SettingsIcon, modes: ["ceo"] },
];

const playBeep = () => {
  try {
    const ctx = new (window.AudioContext || window.webkitAudioContext)();
    const o = ctx.createOscillator();
    const g = ctx.createGain();
    o.connect(g);
    g.connect(ctx.destination);
    o.frequency.value = 880;
    g.gain.value = 0.12;
    o.start();
    o.stop(ctx.currentTime + 0.35);
  } catch (e) {
    console.debug("Notification beep unavailable:", e);
  }
};

export default function AdminLayout() {
  const { user, logout } = useAuth();
  const { mode, viewMode, privacy, togglePrivacy, switchMode } = useMode();
  const navigate = useNavigate();
  const location = useLocation();
  const [pendingCount, setPendingCount] = useState(0);
  const prevCount = useRef(null);
  const [revealOpen, setRevealOpen] = useState(false);
  const [revealPin, setRevealPin] = useState("");
  const [revealBusy, setRevealBusy] = useState(false);

  const handleSwitchClick = () => {
    if (!privacy) { togglePrivacy(); toast.success("Manager Mode — confidential figures hidden"); }
    else { setRevealPin(""); setRevealOpen(true); }
  };

  const submitReveal = async (e) => {
    e.preventDefault();
    if (!revealPin.trim()) return;
    setRevealBusy(true);
    try {
      const { data } = await api.post("/mode/verify", { pin: revealPin.trim() });
      if (data.mode !== "ceo") {
        toast.error("That is not the CEO PIN — figures stay hidden");
      } else {
        togglePrivacy();
        setRevealOpen(false);
        toast.success("CEO Mode — full figures revealed");
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || "Incorrect PIN");
    }
    setRevealBusy(false);
    setRevealPin("");
  };

  useEffect(() => {
    if (!mode || mode === "staff") return;
    const poll = () => {
      api.get("/appointments/pending-count").then(({ data }) => {
        if (prevCount.current !== null && data.count > prevCount.current) playBeep();
        prevCount.current = data.count;
        setPendingCount(data.count);
      }).catch(() => {});
    };
    poll();
    const t = setInterval(poll, 30000);
    return () => clearInterval(t);
  }, [mode]);

  const handleLogout = async () => {
    switchMode();
    await logout();
    navigate("/admin/login");
  };

  if (!mode) return <PinGate />;
  if (mode === "staff" && !location.pathname.startsWith("/admin/staff-day")) return <Navigate to="/admin/staff-day" replace />;
  const navMatches = navItems.filter((i) => (i.end ? location.pathname === i.to : location.pathname.startsWith(i.to)));
  const bestLen = navMatches.reduce((m, i) => Math.max(m, i.to.length), 0);
  const best = navMatches.filter((i) => i.to.length === bestLen);
  if (best.length && !best.some((i) => i.modes.includes(viewMode))) return <Navigate to="/admin" replace />;

  const visibleNav = navItems.filter((i) => i.modes.includes(viewMode));

  return (
    <div className="min-h-screen bg-[#F4F5F4] flex" data-testid="admin-layout">
      <aside className="w-60 bg-[#0A3F2F] text-white flex flex-col fixed inset-y-0 z-40">
        <div className="px-6 py-6 border-b border-white/10">
          <span className="font-serif-brand text-2xl font-semibold">Green</span>
          <p className="text-[9px] uppercase tracking-[0.25em] text-[#C5A059] mt-1">Salon Manager</p>
        </div>
        <nav className="flex-1 px-3 py-6 space-y-1">
          {visibleNav.map((item) => (
            <NavLink key={item.to} to={item.to} end={item.end} data-testid={`sidebar-${item.label.toLowerCase().replace(/ /g, "-")}`}
              className={({ isActive }) =>
                `flex items-center gap-3 px-4 py-2.5 rounded-md text-sm font-medium transition-colors ${
                  isActive ? "bg-[#C5A059] text-[#1C1C1C]" : "text-white/75 hover:bg-white/10 hover:text-white"
                }`
              }>
              <item.icon className="w-4 h-4" />
              {item.label}
            </NavLink>
          ))}
        </nav>
        <div className="px-3 pb-6 space-y-1 border-t border-white/10 pt-4">
          <Link to="/" data-testid="sidebar-view-site" className="flex items-center gap-3 px-4 py-2.5 rounded-md text-sm text-white/75 hover:bg-white/10 hover:text-white transition-colors">
            <Globe className="w-4 h-4" /> View Website
          </Link>
          <button onClick={handleLogout} data-testid="sidebar-logout-btn"
            className="w-full flex items-center gap-3 px-4 py-2.5 rounded-md text-sm text-white/75 hover:bg-white/10 hover:text-white transition-colors">
            <LogOut className="w-4 h-4" /> Logout
          </button>
          <p className="px-4 pt-2 text-[11px] text-white/40 truncate">{user?.email}</p>
        </div>
      </aside>
      <main className="flex-1 ml-60 p-6 lg:p-8">
        <div className="flex items-center justify-end gap-3 mb-6" data-testid="admin-topbar">
          {mode !== "staff" && (
            <button onClick={() => navigate("/admin/appointments")} data-testid="pending-bell-btn"
              className="relative bg-white border border-[#E5E7E5] rounded-full p-2.5 hover:border-[#0A3F2F] transition-colors" title="Pending booking requests">
              <Bell className="w-4 h-4 text-[#0A3F2F]" />
              {pendingCount > 0 && (
                <span data-testid="pending-badge" className="absolute -top-1.5 -right-1.5 bg-red-600 text-white text-[10px] font-bold rounded-full min-w-[18px] h-[18px] flex items-center justify-center px-1">
                  {pendingCount}
                </span>
              )}
            </button>
          )}
          <span data-testid="current-mode-chip" className={`flex items-center gap-1.5 text-[11px] font-bold uppercase tracking-wider px-3 py-1.5 rounded-full ${
            viewMode === "ceo" ? "bg-[#0A3F2F] text-[#C5A059]" : viewMode === "manager" ? "bg-[#C5A059] text-[#1C1C1C]" : "bg-gray-200 text-gray-600"
          }`}>
            {mode === "ceo" && privacy && <EyeOff className="w-3 h-3" />}
            {viewMode === "ceo" ? "CEO Mode" : viewMode === "manager" ? "Manager Mode" : "Staff Mode"}
          </span>
          {mode === "ceo" ? (
            <>
              <button onClick={handleSwitchClick} data-testid="switch-mode-btn"
                title={privacy ? "Unlock CEO figures (PIN required)" : "Hide confidential figures instantly"}
                className="flex items-center gap-2 bg-white border border-[#E5E7E5] text-[#5C6A63] text-xs font-semibold px-4 py-2 rounded-full hover:border-[#0A3F2F] hover:text-[#0A3F2F] transition-colors">
                {privacy ? <Eye className="w-3.5 h-3.5" /> : <EyeOff className="w-3.5 h-3.5" />} Switch Mode
              </button>
              <button onClick={switchMode} data-testid="lock-mode-btn" title="Lock — change role with PIN"
                className="bg-white border border-[#E5E7E5] text-[#5C6A63] p-2.5 rounded-full hover:border-[#0A3F2F] hover:text-[#0A3F2F] transition-colors">
                <Lock className="w-3.5 h-3.5" />
              </button>
            </>
          ) : (
            <button onClick={switchMode} data-testid="switch-mode-btn"
              className="flex items-center gap-2 bg-white border border-[#E5E7E5] text-[#5C6A63] text-xs font-semibold px-4 py-2 rounded-full hover:border-[#0A3F2F] hover:text-[#0A3F2F] transition-colors">
              <RefreshCcw className="w-3.5 h-3.5" /> Switch Mode
            </button>
          )}
        </div>
        <Outlet />
        <Dialog open={revealOpen} onOpenChange={setRevealOpen}>
          <DialogContent className="max-w-xs">
            <DialogHeader><DialogTitle>Unlock CEO Mode</DialogTitle></DialogHeader>
            <form onSubmit={submitReveal} className="space-y-3" data-testid="reveal-pin-form">
              <p className="text-xs text-[#5C6A63]">Enter your CEO PIN to reveal revenue, profit and charts.</p>
              <input data-testid="reveal-pin-input" type="password" inputMode="numeric" autoFocus maxLength={8}
                className="w-full border border-[#E5E7E5] rounded-md px-3 py-2.5 text-center text-lg tracking-[0.4em] focus:outline-none focus:border-[#0A3F2F]"
                placeholder="••••" value={revealPin} onChange={(e) => setRevealPin(e.target.value)} />
              <button type="submit" disabled={revealBusy} data-testid="reveal-pin-submit"
                className="w-full bg-[#0A3F2F] text-white font-semibold py-2.5 rounded-full text-sm hover:bg-[#0d5540] disabled:opacity-50">
                {revealBusy ? "Checking…" : "Unlock & Reveal"}
              </button>
            </form>
          </DialogContent>
        </Dialog>
      </main>
    </div>
  );
}
